import { metrics, type AccountingMode } from "@north-beam/nb-common";

import { useSelectedIsDirtyMetric, useSelectedMetric } from "../atoms";
import { formulaAstToTokens } from "../utils/formula-ast-to-tokens";

import { useFormulaBuilder } from "./use-formula-builder";

export const useCurrentlyUpdatingMetric = () => {
  const [currentlyUpdatingMetric, setCurrentlyUpdatingMetric] =
    useSelectedMetric();

  const [isDirty, setMetricIsDirty] = useSelectedIsDirtyMetric();

  const { updateFormula } = useFormulaBuilder();

  const createCurrentlyUpdatingMetric = (
    customMetric?: metrics.CustomMetric,
  ) => {
    const newMetric: metrics.CustomMetric = {
      id: customMetric?.id,
      name: customMetric?.name ?? "",
      accounting_mode: customMetric?.accounting_mode ?? "accrual",
      description: customMetric?.description ?? "",
      data_type: customMetric?.data_type ?? metrics.MetricTypes.INTEGER,
      is_negative_trend_good: customMetric?.is_negative_trend_good ?? false,
      formula: customMetric?.formula ?? null,
      isTemplate: customMetric?.isTemplate ?? null,
    } as const;
    setCurrentlyUpdatingMetric(newMetric);
    if (newMetric.formula) {
      updateFormula(formulaAstToTokens(newMetric.formula));
    } else {
      updateFormula([]);
    }
  };

  const updateCustomMetric = <Field extends keyof metrics.CustomMetric>(
    field: Field,
    value: metrics.CustomMetric[Field],
  ) => {
    if (!currentlyUpdatingMetric) {
      return;
    }

    setMetricIsDirty(true);
    setCurrentlyUpdatingMetric({
      ...currentlyUpdatingMetric,
      [field]: value,
    });
  };

  return {
    isDirty,
    id: currentlyUpdatingMetric?.id,
    currentlyUpdatingMetric,
    setCurrentlyUpdatingMetric,
    isEditing: Boolean(currentlyUpdatingMetric),
    isCreatingNew:
      currentlyUpdatingMetric && currentlyUpdatingMetric?.id === undefined,
    createCurrentlyUpdatingMetric,
    accountingMode:
      (currentlyUpdatingMetric?.accounting_mode as AccountingMode) ?? "accrual",
    resetCurrentlyUpdatingMetric: () => setCurrentlyUpdatingMetric(undefined),
    name: currentlyUpdatingMetric?.name ?? "",
    setName: (name: string) => updateCustomMetric("name", name),
    setAccountingMode: (accountingMode: string) =>
      updateCustomMetric("accounting_mode", accountingMode),
    isNegativeTrendGood:
      currentlyUpdatingMetric?.is_negative_trend_good ?? false,
    setIsNegativeTrendGood: (isNegativeTrendGood: boolean) =>
      updateCustomMetric("is_negative_trend_good", isNegativeTrendGood),
    dataType: currentlyUpdatingMetric?.data_type ?? metrics.MetricTypes.INTEGER,
    setDataType: (dataType: metrics.MetricType) =>
      updateCustomMetric("data_type", dataType),
    setFormula: (formula: metrics.AstNode) =>
      updateCustomMetric("formula", formula),
    description: currentlyUpdatingMetric?.description ?? "",
    setDescription: (description: string) =>
      updateCustomMetric("description", description),
  };
};
